import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FormHandles } from '@unform/core';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { ICompany } from './types';
import { useRequest } from '../../hooks/RequestContext';
import { companiesRouteApi } from '../../routes/config/api';
import { listCompaniesRoute, showCompanyRoute } from '../../routes/config';
import isBlank from '../../utils/isBlank';
import getValidationsErrors from '../../utils/errors/getValidationsErrors';

import LayoutEdit from '../../components/Layouts/Edit';
import Form from './Form';

interface IEditParams {
  id: string;
}

export const Edit: React.FC = () => {
  const [initialData, setInitialData] = useState<ICompany>({} as ICompany);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const formRef = useRef<FormHandles>(null);
  const { id: companyId } = useParams<IEditParams>();
  const history = useHistory();
  const { get: requestGet, put: requestPut } = useRequest();

  const updateCompany = useCallback(
    async (data: any) => {
      try {
        setIsSaving(true);

        const response = await requestPut(
          `${companiesRouteApi.path}/${companyId}`,
          data,
        );

        toast.success("Empresa atualizada com sucesso!");
        history.push(showCompanyRoute.build({ id: response.id }));
      } catch (err) {
        toast.error("Ops, não foi possível atualizar os dados da empresa!");
      } finally {
        setIsSaving(false);
      }
    },
    [requestPut, companyId, history]
  );

  const handleSubmit = useCallback(
    async () => {
      try {
        formRef.current?.setErrors({});

        const data = formRef.current?.getData() as any;
        const schema = Yup.object().shape({
          cnpj: Yup.string().required('Campo obrigatório'),
          company_name: Yup.string().required('Campo obrigatório'),
          email: Yup.string().required('Campo obrigatório'),
          phone: Yup.string().required('Campo obrigatório'),
        });

        await schema.validate(data, { abortEarly: false });

        updateCompany(data);
      } catch (err) {
        const validationErrors = getValidationsErrors(err);
        formRef.current?.setErrors(validationErrors);
      }
    },
    [updateCompany]
  );

  useEffect(() => {
    async function findCompany() {
      try {
        const response = await requestGet(`${companiesRouteApi.path}/${companyId}`);
        if (!isBlank(response)) {
          setInitialData({
            ...response,
            profile: {
              label: response.profile?.name,
              value: response.profile?.id,
            }
          });
        }
      } catch {
        toast.error('Ops, ocorreu um problema. Tente novamente mais tarde!');
      } finally {
        setLoading(false);
      }
    }

    findCompany();
  }, [requestGet, companyId]);

  return (
    <LayoutEdit
      breadcrumbs={[
        { text: 'Empresas', path: listCompaniesRoute.path },
        { text: 'Editar Empresa' }
      ]}
      cardProps={{
        title: 'Alterar informações da empresa',
        noData: isBlank(initialData),
        loading,
      }}
      footerActionsProps={{
        onCancelRoute: listCompaniesRoute.path,
        onSubmit: handleSubmit,
        loading: isSaving || loading,
      }}
    >
      <Form
        formRef={formRef}
        isEditPage
        initialData={initialData}
      />
    </LayoutEdit>
  );
}
